import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageLoader } from '../../../shared/ui/Spinner'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { confirm } from '../../../shared/ui/confirmStore'
import { useQuotesSelection } from './hooks/useQuotesSelection'
import { useColumnWidths } from './hooks/useColumnWidths'
import { QuotesSelectionHeader } from './components/QuotesSelectionHeader'
import { QuotesSummaryCards } from './components/QuotesSummaryCards'
import { QuotesFilterBar } from './components/QuotesFilterBar'
import { PositionCard } from './components/PositionCard'

/**
 * Страница «Выбор квот» — сводка позиций заявки с их квотами на одном экране.
 * Менеджер помечает квоты радио-кнопкой, затем сохраняет батчем.
 * Существующая страница позиции (RequestItemPage) остаётся — ссылка на неё
 * из парт-номера. Показываем все 5 матриц (Склад/Китай/РФ/Производители/Стоки),
 * раньше было ограничение только на закупочные (Китай/Производители).
 */
export function QuotesSelectionPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const state = useQuotesSelection(id)
  const { widths, onStartResize } = useColumnWidths()

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  // Confirm при уходе с несохранёнными изменениями (перезагрузка/закрытие вкладки).
  useEffect(() => {
    if (!state.isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [state.isDirty])

  // Сколько позиций изменится при нажатии «Авто: минимальные цены»:
  // позиции, где есть квота isMin и выбор сейчас не равен ей (либо не выбран,
  // либо выбрана более дорогая квота). Кнопка disabled только если менять
  // нечего (у всех уже выбрана min-квота или min-квот нет вовсе).
  const autoCandidates = useMemo(() => {
    if (!state.data) return 0
    return state.data.positions.filter((p) => {
      const minQuote = p.quotes.find((q) => q.isMin)
      if (!minQuote) return false
      return (state.selection[p.id] ?? null) !== minQuote.id
    }).length
  }, [state.data, state.selection])

  if (state.isLoading) return <PageLoader />
  if (state.error) return <ErrorMessage error={state.error} />
  if (!state.data) return null

  const { data } = state
  const allCollapsed = collapsed.size >= data.positions.length && data.positions.length > 0

  const handleCollapseAll = () => setCollapsed(new Set(data.positions.map((p) => p.id)))
  const handleExpandAll = () => setCollapsed(new Set())
  const toggleCollapsed = (posId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(posId)) next.delete(posId)
      else next.add(posId)
      return next
    })
  }

  const handleAutoSelect = async () => {
    const candidates = autoCandidates
    if (candidates === 0) return

    // Подозрительные позиции: один закупщик у одного поставщика прислал
    // несколько квот с разными датами → цена могла обновиться, надо
    // проверить вручную. Берём только календарную дату (без времени), чтобы
    // не считать разными несколько квот, залитых одной выгрузкой.
    const stale = (state.data?.positions ?? []).filter((p) => {
      if (p.quotes.length < 2) return false
      const byKey = new Map<string, Set<string>>()
      for (const q of p.quotes) {
        const day = q.createdAt.slice(0, 10)
        const key = `${q.procurementUserId}::${q.supplierName.trim().toLowerCase()}`
        const dates = byKey.get(key) ?? new Set()
        dates.add(day)
        byKey.set(key, dates)
      }
      for (const dates of byKey.values()) {
        if (dates.size >= 2) return true
      }
      return false
    })

    let message =
      `В ${candidates} позициях будет выбрана минимальная квота. ` +
      `Текущий выбор в них будет перезаписан.`
    if (stale.length > 0) {
      const sample = stale
        .slice(0, 5)
        .map((p) => p.partNumber)
        .join(', ')
      const more = stale.length > 5 ? ` и ещё ${stale.length - 5}` : ''
      message +=
        `\n\nВнимание: в ${stale.length} позиц. (${sample}${more}) у одного закупщика ` +
        `от одного поставщика есть квоты с разными датами — цены могли обновиться. ` +
        `Проверьте актуальные цены вручную.`
    }
    if (!(await confirm({ title: 'Выбрать минимальные квоты?', message, confirmText: 'Выбрать' })))
      return
    state.autoSelectMin()
  }

  const handleSave = async () => {
    try {
      await state.save()
    } catch {
      /* ошибка отображается через state.saveError */
    }
  }

  const handleBack = async () => {
    if (state.isDirty) {
      const ok = await confirm({
        title: 'Есть несохранённые изменения.',
        message: 'Уйти без сохранения?',
        confirmText: 'Уйти',
      })
      if (!ok) return
    }
    navigate(`/requests/${id}`)
  }

  if (!data.hasProcurementItems) {
    return (
      <div className="space-y-4 pb-10">
        <QuotesSelectionHeader request={data.request} />
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 text-center text-sm text-gray-600 dark:text-gray-400">
          В этой заявке нет активных позиций для выбора квот.
          <div className="mt-3">
            <button
              type="button"
              onClick={handleBack}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-900"
            >
              Вернуться к заявке
            </button>
          </div>
        </div>
      </div>
    )
  }

  const saveErrorMessage =
    state.saveError instanceof Error
      ? state.saveError.message
      : state.saveError
        ? 'Ошибка сохранения'
        : null

  return (
    <div className="space-y-5 pb-10">
      <QuotesSelectionHeader request={data.request} />

      {/* Sticky-блок со сводкой и тулбаром: при длинном списке позиций
          всегда виден счётчик «Квота выбрана X/Y» и кнопка сохранения. */}
      <div className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-950 pt-1 pb-3 -mx-1 px-1 space-y-3 border-b border-gray-200/60 dark:border-gray-800/60">
        <QuotesSummaryCards
          summary={data.summary}
          positions={data.positions}
          localSelection={state.selection}
          filter={state.filter}
          onFilterChange={state.setFilter}
        />

        <QuotesFilterBar
          filter={state.filter}
          onFilterChange={state.setFilter}
          onCollapseAll={handleCollapseAll}
          onExpandAll={handleExpandAll}
          allCollapsed={allCollapsed}
          onAutoSelectMin={handleAutoSelect}
          autoCandidates={autoCandidates}
          isDirty={state.isDirty}
          isSaving={state.isSaving}
          saveError={saveErrorMessage}
          onSave={() => void handleSave()}
        />
      </div>

      <div className="space-y-3">
        {state.filteredPositions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Нет позиций под выбранный фильтр.
          </div>
        ) : (
          state.filteredPositions.map((p) => (
            <PositionCard
              key={p.id}
              position={p}
              localSelectedQuoteId={state.selection[p.id] ?? null}
              collapsed={collapsed.has(p.id)}
              onToggleCollapsed={() => toggleCollapsed(p.id)}
              onSelectQuote={(qid) => state.setSelection(p.id, qid)}
              columnWidths={widths}
              onStartResize={onStartResize}
            />
          ))
        )}
      </div>
    </div>
  )
}
